import { useState, useEffect, memo } from "react";
import { Row, Col, Typography, Input, Button, Badge, Popover } from "antd";
import { EditOutlined } from "@ant-design/icons";

import FieldJoinModal from "./FieldJoinModal";
import FieldFilterModal from "./FieldFilterModal";
import FieldTransformModal from "./FieldTransformModal";

import "./steps.css";

const { Text } = Typography;

const FieldMappingRow = ({
  disabled,
  connectionType,
  joinForm,
  filterForm,
  transformForm,
  field,
  fieldPath,
  currentMapping,
  onMappingChange,
}) => {
  const [joinModalVisible, setJoinModalVisible] = useState(false);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [transformModalVisible, setTransformModalVisible] = useState(false);
  const [descriptionOpen, setDescriptionOpen] = useState(false);

  // store local state for preventing delayed rendering
  const [apiCorrespondingKey, setApiCorrespondingKey] = useState("");
  const [tableValue, setTableValue] = useState("");
  const [columnValue, setColumnValue] = useState("");

  const [joinConditionsValue, setJoinConditionsValue] = useState([]);
  const [filterConditionsValue, setFilterConditionsValue] = useState({});
  const [transformationsValue, setTransformationsValue] = useState({});

  const [descriptionValue, setDescriptionValue] = useState("");

  useEffect(() => {
    setApiCorrespondingKey(currentMapping?.["source"]?.["correspondingKey"] || "");
    setTableValue(currentMapping?.["source"]?.["table"] || "");
    setColumnValue(currentMapping?.["source"]?.["column"] || "");
    setDescriptionValue(currentMapping?.["source"]?.["description"] || "");
  }, [currentMapping]);

  function openConditionModal(condition) {
    let conditionForm;
    let setConditionModalVisible;
    let draftValues;
    let hasDraftValues = false;
    switch (condition) {
      case "joinConditions":
        conditionForm = joinForm;
        setConditionModalVisible = setJoinModalVisible;
        draftValues = { joinConditions: joinConditionsValue };
        hasDraftValues = joinConditionsValue.length > 0;
        break;
      case "filterConditions":
        conditionForm = filterForm;
        setConditionModalVisible = setFilterModalVisible;
        draftValues = filterConditionsValue;
        hasDraftValues = Object.keys(filterConditionsValue).length > 0;
        break;
      case "transformations":
        conditionForm = transformForm;
        setConditionModalVisible = setTransformModalVisible;
        draftValues = transformationsValue;
        hasDraftValues = Object.keys(transformationsValue).length > 0;
        break;
      default:
        return;
    }

    if (hasDraftValues) {
      conditionForm.setFieldsValue(draftValues);
    } else if (currentMapping?.["source"]?.[condition]) {
      conditionForm.setFieldsValue(
        condition === "joinConditions"
          ? currentMapping["source"]
          : currentMapping["source"][condition]
      );
    } else {
      conditionForm.resetFields();
    }

    setConditionModalVisible(true);
  }

  function handleJoinConditionsValuesChange(_, allValues) {
    setJoinConditionsValue(allValues?.joinConditions || []);
  };

  function handleFilterConditionsValuesChange(_, allValues) {
    setFilterConditionsValue(allValues || {});
  };

  function handleTransformationsChange(_, allValues) {
    setTransformationsValue(allValues || {});
  };

  function checkTargetHasSet(target) {
    switch (target) {
      case "joinConditions":
        return (currentMapping?.["source"]?.["joinConditions"]?.length || 0) > 0 || joinConditionsValue.length > 0;
      case "filterConditions":
        return Boolean(currentMapping?.["source"]?.["filterConditions"]) || Object.keys(filterConditionsValue).length > 0;
      case "transformations":
        return Boolean(currentMapping?.["source"]?.["transformations"]) || Object.keys(transformationsValue).length > 0;
      default:
        return false;
    }
  };

  return (
    <Row gutter={16} style={{ marginBottom: 16 }}>
      <Col span={4}>
        <Text strong>{field["label"]}</Text>
        <div className="field-description">{field["description"]}</div>
      </Col>
      {connectionType === "api" ? (
        <Col span={19}>
          <Input
            disabled={disabled}
            placeholder="回傳資料中對應的欄位，不同階層請用 “.” 隔開，例如： customer.name"
            value={apiCorrespondingKey}
            onChange={(e) => setApiCorrespondingKey(e.target.value)}
            onBlur={(e) => {
              const newMapping = {
                ...currentMapping,
                source: {
                  ...currentMapping?.["source"],
                  correspondingKey: e.target.value,
                },
              };
              onMappingChange(fieldPath, newMapping);
            }}
          />
        </Col>
      ) : (
        <>
          <Col span={5}>
            <Input
              disabled={disabled}
              placeholder="資料表名稱"
              value={tableValue}
              onChange={(e) => setTableValue(e.target.value)}
              onBlur={(e) => {
                const newMapping = {
                  ...currentMapping,
                  source: {
                    ...currentMapping?.["source"],
                    table: e.target.value,
                    column: currentMapping?.["source"]?.["column"] || "",
                  },
                };
                onMappingChange(fieldPath, newMapping);
              }}
            />
          </Col>
          <Col span={5}>
            <Input
              disabled={disabled}
              placeholder="欄位名稱"
              value={columnValue}
              onChange={(e) => setColumnValue(e.target.value)}
              onBlur={(e) => {
                const newMapping = {
                  ...currentMapping,
                  source: {
                    ...currentMapping?.["source"],
                    table: currentMapping?.["source"]?.["table"] || "",
                    column: e.target.value,
                  },
                };
                onMappingChange(fieldPath, newMapping);
              }}
            />
          </Col>
          <Col span={3}>
            {joinModalVisible ? (
              <FieldJoinModal
                disabled={disabled}
                open={joinModalVisible}
                setOpen={setJoinModalVisible}
                joinForm={joinForm}
                fieldPath={fieldPath}
                handleFieldMappingChange={onMappingChange}
                onValuesChange={handleJoinConditionsValuesChange}
              />
            ) : null}
            <Badge
              dot={true}
              count={checkTargetHasSet("joinConditions") ? 1 : 0}
              style={{ backgroundColor: "#52c41a" }}
            >
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openConditionModal("joinConditions")}
              >
                JOIN 條件
              </Button>
            </Badge>
          </Col>
          <Col span={3}>
            {filterModalVisible ? (
              <FieldFilterModal
                disabled={disabled}
                open={filterModalVisible}
                setOpen={setFilterModalVisible}
                filterForm={filterForm}
                fieldPath={fieldPath}
                handleFieldMappingChange={onMappingChange}
                onValuesChange={handleFilterConditionsValuesChange}
              />
            ) : null}
            <Badge
              dot={true}
              count={checkTargetHasSet("filterConditions") ? 1 : 0}
              style={{ backgroundColor: "#52c41a" }}
            >
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openConditionModal("filterConditions")}
              >
                篩選條件
              </Button>
            </Badge>
          </Col>
          <Col span={3}>
            {transformModalVisible ? (
              <FieldTransformModal
                disabled={disabled}
                open={transformModalVisible}
                setOpen={setTransformModalVisible}
                transformForm={transformForm}
                fieldPath={fieldPath}
                handleFieldMappingChange={onMappingChange}
                onValuesChange={handleTransformationsChange}
              />
            ) : null}
            <Badge
              dot={true}
              count={checkTargetHasSet("transformations") ? 1 : 0}
              style={{ backgroundColor: "#52c41a" }}
            >
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openConditionModal("transformations")}
              >
                轉換規則
              </Button>
            </Badge>
          </Col>
        </>
      )}
      <Col span={1}>
        <Badge
          dot={true}
          count={descriptionValue ? 1 : 0}
          style={{ backgroundColor: "#52c41a" }}
        >
          <Popover
            open={descriptionOpen}
            onOpenChange={setDescriptionOpen}
            content={
              descriptionOpen ? (
                <Input.TextArea
                  disabled={disabled}
                  rows={4}
                  value={descriptionValue}
                  onChange={(e) => setDescriptionValue(e.target.value)}
                  onBlur={(e) => {
                    const newMapping = {
                      ...currentMapping,
                      source: {
                        ...currentMapping?.["source"],
                        description: e.target.value,
                      },
                    };
                    onMappingChange(fieldPath, newMapping);
                  }}
                />
              ) : null
            }
            trigger="click"
          >
            <Button size="small" icon={<EditOutlined />} onClick={() => setDescriptionOpen(true)} />
          </Popover>
        </Badge>
      </Col>
    </Row>
  );
};

export default memo(FieldMappingRow);
